import { useEffect, useRef } from 'react';
import { 
  registerScrollLock, 
  unregisterScrollLock, 
  enforceScrollState,
  clearScrollStyles 
} from './scrollLockState';

// Singleton state for scroll lock management
let lockCount = 0;
let savedScrollY = 0;

const lockScroll = () => {
  if (lockCount === 0) {
    savedScrollY = window.scrollY;
    // Lock both html and body for consistency
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }
  lockCount++;
};

const unlockScroll = () => {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    // Clear both html and body styles
    clearScrollStyles(document.documentElement);
    clearScrollStyles(document.body);
    document.body.style.paddingRight = '';
    document.body.style.touchAction = '';
    
    // Use rAF to ensure layout recomputes before restoring scroll
    const scrollY = savedScrollY;
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }
};

// Force reset all locks (for recovery from stale state)
export const forceUnlockScroll = () => {
  lockCount = 0;
  // Clear both html and body completely
  clearScrollStyles(document.documentElement);
  clearScrollStyles(document.body);
  document.body.style.paddingRight = '';
  document.body.style.touchAction = '';
  
  // Also enforce via state
  enforceScrollState();
};

export const useScrollLock = (isLocked: boolean, reason: string = 'unknown') => {
  const wasLockedRef = useRef(false);

  useEffect(() => {
    if (isLocked && !wasLockedRef.current) {
      registerScrollLock(reason);
      lockScroll();
      wasLockedRef.current = true;
    } else if (!isLocked && wasLockedRef.current) {
      unregisterScrollLock(reason);
      unlockScroll();
      wasLockedRef.current = false;
    }

    return () => {
      if (wasLockedRef.current) {
        unregisterScrollLock(reason);
        unlockScroll();
        wasLockedRef.current = false;
      }
    };
  }, [isLocked, reason]);
};
